import { BaseEnv } from './base.js';
import { compareVersion } from '../check.js';
import { urlParse } from '../url.js';
import { App, voghionConfig, mocasaShoppingConfig } from '../app-config.js';
import { getDeviceCode } from '../device.js';
export var Gender;
(function (Gender) {
    Gender["Boy"] = "1";
    Gender["Girl"] = "2";
    Gender["Secret"] = "3";
})(Gender = Gender || (Gender = {}));
export var Channel;
(function (Channel) {
    Channel["h5"] = "1";
    Channel["android"] = "2";
    Channel["iOS"] = "3";
    Channel["pc"] = "4";
})(Channel = Channel || (Channel = {}));
/**
 * 环境类，主要是基于 hybrid 环境扩展
 */
export class Env extends BaseEnv {
    /**
     * 返回全局单例
     */
    static getInstance(options = {}) {
        if (!options) {
            options = {};
        }
        const hasCustomOption = JSON.stringify(options) !== '{}';
        if (!Env.instance) {
            Env.instance = new Env(options);
        }
        else if (hasCustomOption) {
            Env.instance.init(options);
        }
        return Env.instance;
    }
    constructor(options = {}) {
        super(options);
        this.isAppHome = false; // 是否显示在 app 首页(tab)，不显示返回按钮
        this.isBottomSheet = false; // 是否显示在底部浮层,不控制原生导航 eg:直播详情页面
        this.isAppSubTab = false; // 是否在 app 原生页面的子 tab 中，此时导航栏由客户端控制
        this.isNativePage = false; // 是否嵌入原生页面
        this.mobileModel = ''; // 客户端返回的设备型号，用于一些特殊判断
        this.iOSPerformanceModel = 9; // iOS 高性能最低型号，iphone7
        this.androidPerformanceVersion = '8.0.0'; // android 高性能最低版本
        this.visibilityState = 'visible'; // webView可见性，不等于 document.visibilityState，只在原生页面嵌套 webView 的场景下有用
        this.systemInfo = {}; // 系统信息
        this._apiBaseUrl = ''; // api 基础路径
        this._webBaseUrl = ''; // web 基础路径
        this._enableImmersiveUI = false; // 是否开启沉浸式，布局从状态栏开始
        this._immersiveUIChanged = false;
        this._appConfigs = [];
        this._hasNativeLoading = false;
        this._clientInfo = null;
        this._commonInfo = null;
        this.init(options);
    }
    /**
     * 对环境进行初始化配置
     * @param options
     */
    init(options = {}) {
        let appConfigs = options.appConfigs || [];
        if (!appConfigs.length) {
            appConfigs = [voghionConfig, mocasaShoppingConfig];
        }
        this._appConfigs = appConfigs;
        this.initApp();
    }
    initApp() {
        const ua = this.ua;
        for (const config of this._appConfigs) {
            const uaList = config.ua;
            for (const item of uaList) {
                const uaItem = item + '/';
                config.active = ua.indexOf(uaItem) > -1;
                if (config.active) {
                    const re = new RegExp(`${uaItem}[0-9.]+`, 'g');
                    const r = re.exec(ua);
                    if (r !== null) {
                        config.version = r[0].replace(uaItem, '');
                    }
                    break;
                }
            }
            if (config.active) {
                break;
            }
        }
    }
    refreshFromClient() {
        super.refreshFromClient();
        const screenHeight = window.screen.height;
        const blankHeight = screenHeight - window.innerHeight;
        this.isBottomSheet = blankHeight / screenHeight > 0.29;
        this._hasNativeLoading = true;
        window.addEventListener('load', () => {
            setTimeout(() => {
                this._hasNativeLoading = false;
            }, 50);
        });
    }
    refreshWithUa(userAgent) {
        super.refreshWithUa(userAgent);
        this.checkImmersiveUI();
    }
    refreshWithHref(href) {
        super.refreshWithHref(href);
        if (href) {
            const { search } = urlParse(href);
            // 通过链接参数判断网页是位置
            this.isAppHome = search.indexOf('inAppHome=1') > 0;
            this.isAppSubTab = search.indexOf('inAppSubTab=1') > 0;
        }
    }
    set apiBaseUrl(val) {
        this._apiBaseUrl = val;
    }
    get apiBaseUrl() {
        return this._apiBaseUrl;
    }
    set webBaseUrl(val) {
        this._webBaseUrl = val;
    }
    get webBaseUrl() {
        return this._webBaseUrl;
    }
    /**
     * 当前 app 版本号
     */
    get currentAppVersion() {
        const apps = this._appConfigs.filter((r) => r.active);
        return apps.length ? apps[0].version : '';
    }
    /**
     * 当前环境是否支持沉浸式UI
     */
    get isSupportedImmersiveUI() {
        return this.isIOS || (this.isAndroid && compareVersion(this.androidVersion, '5.0.0') >= 0);
    }
    /**
     * 检查是否开启了沉浸式，经过测试，很难根据高度判断，所以只判断版本，新版本app提供了检测接口isStatusBarHidden
     * @param autoSet
     */
    checkImmersiveUI(autoSet = true) {
        const enableImmersiveUI = this.isSupportedImmersiveUI;
        if (autoSet && !this._immersiveUIChanged) {
            this._enableImmersiveUI = enableImmersiveUI;
        }
        return enableImmersiveUI;
    }
    /**
     * 切换沉浸式UI
     * @param val
     */
    switchImmersiveUI(val = true) {
        if (!this.navBarLocked) {
            if (this.isSupportedImmersiveUI) {
                this._immersiveUIChanged = true;
                if (val) {
                    this._enableImmersiveUI = true;
                }
                else {
                    this._enableImmersiveUI = false;
                }
                return true;
            }
        }
        return false;
    }
    /**
     * 获取 app 的配置
     * @param app 如果不指定，返回当前激活的 app
     * @returns
     */
    getAppConfig(app) {
        const appConfigs = this._appConfigs || [];
        const configs = app ? appConfigs.filter((r) => r.app === app) : appConfigs.filter((r) => r.active);
        return configs.length ? configs[0] : null;
    }
    /**
     * 是否是指定 app
     * @param app 如果不指定，返回当前是否有激活的 app
     * @returns
     */
    isApp(app) {
        const config = this.getAppConfig(app);
        return config ? config.active : false;
    }
    /**
     * 获取 app 版本号
     * @param app 如果不指定，返回当前激活的 app 版本号
     * @returns
     */
    getAppVersion(app) {
        const config = this.getAppConfig(app);
        return config ? config.version : '';
    }
    /**
     * 设置开启沉浸式UI，高版本app提供JS隐藏状态栏接口后可用
     */
    set enableImmersiveUI(val) {
        this.switchImmersiveUI(val);
    }
    /**
     * 是否开启沉浸式UI
     */
    get enableImmersiveUI() {
        return this._enableImmersiveUI;
    }
    /**
     * 是否存在客户端loading，如果存在不弹出JS loading
     */
    get hasNativeLoading() {
        return this._hasNativeLoading;
    }
    /**
     * 是否新开webview打开页面，在高性能手机上可以新开webview带来更好的操作体验
     */
    get enableNewWebView() {
        let res = false;
        if (this.isEnvAppWebViewClient) {
            if (this.isIOS) {
                if (this.screenHeight > 750) {
                    res = true;
                }
                else if (this.mobileModel) {
                    const model = this.mobileModel.toLowerCase().replace('iphone', '');
                    const arr = model.split(',');
                    if (parseInt(arr[0]) >= this.iOSPerformanceModel) {
                        res = true;
                    }
                }
            }
            else {
                if (window.navigator.serviceWorker &&
                    compareVersion(this.androidVersion, this.androidPerformanceVersion) >= 0) {
                    res = true;
                }
            }
        }
        return res;
    }
    /**
     * 是否是站外 app 环境
     */
    get isEnvOutsideApp() {
        return !this.isEnvAppWebView;
    }
    /**
     * 是否从 app webview 发起的请求，主要用于服务端渲染
     */
    get requestFromAppWebView() {
        return this.isEnvAppWebView && !this._isWebDev;
    }
    /**
     * 是否是 webView.client 环境
     */
    get isEnvAppWebViewClient() {
        return this.isEnvAppWebView && this.isClient;
    }
    /**
     * 导航栏是否锁定，此时不能调用 nativeApi 接口控制原生导航
     */
    get navBarLocked() {
        return this.isAppSubTab || this.isBottomSheet || this.isNativePage;
    }
    /**
     * 是否是 webView 环境
     */
    get isEnvAppWebView() {
        const activeApps = this._appConfigs.filter((r) => r.active);
        return activeApps.length > 0;
    }
    /**
     * 当前的应用和配置
     */
    get appConfigs() {
        return this._appConfigs;
    }
    /**
     * 获取设备信息
     */
    get clientInfo() {
        if (!this._clientInfo) {
            const appSystem = this.isDesktop ? 'pc' : this.isEnvAppWebView ? (this.isIOS ? 'iOS' : 'android') : 'h5';
            const app = this.getAppConfig();
            return {
                appName: app && app.name ? app.name : 'Voghion',
                appSystem: appSystem,
                tradeName: appSystem,
                appSystemVersion: this.isDesktop ? '10.0' : this.isIOS ? this.iOSVersion : this.androidVersion,
                appVersion: '9.9.9',
                channel: this.isDesktop ? Channel.pc : Channel.h5,
                gender: Gender.Secret,
                country: 'DE',
                deviceCode: getDeviceCode(this.isDesktop ? 'pc' : 'h5'),
                interfaceVersion: '999',
                language: 'en',
                systemLanguage: 'en',
                netType: 'wifi',
                phoneType: '',
                userToken: '',
                currency: 'EUR'
            };
        }
        return this._clientInfo;
    }
    /**
     * 更新设备信息
     */
    set clientInfo(value) {
        this._clientInfo = value;
    }
    /**
     * 获取公共信息
     */
    get commonInfo() {
        return (this._commonInfo || {
            userId: '',
            deviceCode: getDeviceCode(this.isDesktop ? 'pc' : 'h5'),
            statusBarHeight: this.statusBarHeight,
            priceRuler: '#<#€#,#.#',
            inAppTab: false
        });
    }
    /**
     * 更新公共信息
     */
    set commonInfo(value) {
        this._commonInfo = value;
    }
}
Env.instance = null;
/**
 * @deprecated 直接使用 Env.getInstance
 */
export const getEnv = Env.getInstance;
/**
 * 追加对应的应用 ua 标识
 * @param app
 * @param ua
 */
export const appendAppUA = (app, ua) => {
    switch (app) {
        case App.Voghion:
            if (!voghionConfig.ua.includes(ua)) {
                voghionConfig.ua.push(ua);
            }
            break;
        case App.MocasaShopping:
            if (!mocasaShoppingConfig.ua.includes(ua)) {
                mocasaShoppingConfig.ua.push(ua);
            }
            break;
    }
    if (Env.instance) {
        const env = Env.getInstance();
        for (const appConfig of env.appConfigs) {
            if (appConfig.app === app) {
                if (!appConfig.ua.includes(ua)) {
                    appConfig.ua.push(ua);
                }
                break;
            }
        }
        env.initApp();
    }
};
